/*
  planta-squircle.js — app-wide Figma "corner smoothing" (squircle) via clip-path.

  Why JS and not a static CSS rule:
    Figma corner smoothing is a superellipse, not a border-radius. The only
    scalable, no-SVG-mask way to reproduce it is a clip-path: path(), but
    path() coordinates are absolute pixels — so the path must be regenerated
    for each element's actual size. This helper does that once per element and
    again whenever the element resizes. Works in mobile Safari; clips children
    (e.g. images) too.

  Usage (any element, any size, anywhere in the theme):
    <span class="tile" data-squircle data-squircle-radius="20" data-squircle-smoothing="0.6"></span>
      data-squircle-radius     corner radius in px      (default 20)
      data-squircle-smoothing  Figma smoothing 0..1     (default 1 = full, per design)

  Keep border-radius on the element too — it's the fallback before JS runs and
  for engines without clip-path: path().
*/
(function () {
  'use strict';

  var DEG = Math.PI / 180;

  // Port of Figma's corner-smoothing math (phamfoo/figma-squircle), single radius.
  function cornerParams(radius, smoothing, budget) {
    var p = Math.min((1 + smoothing) * radius, budget);
    var arcAngle = 90 * (1 - smoothing);
    var arcSection = Math.sin((arcAngle / 2) * DEG) * radius * Math.SQRT2;
    var alpha = (90 - arcAngle) / 2;
    var p3p4 = radius * Math.tan((alpha / 2) * DEG);
    var beta = 45 * smoothing;
    var c = p3p4 * Math.cos(beta * DEG);
    var d = c * Math.tan(beta * DEG);
    var b = (p - arcSection - c - d) / 3;
    var a = 2 * b;
    return { a: a, b: b, c: c, d: d, p: p, arc: arcSection, r: radius };
  }

  // Build a rounded-rectangle squircle path for a w×h box with a uniform radius.
  function squirclePath(w, h, radius, smoothing) {
    var budget = Math.min(w, h) / 2;
    radius = Math.min(radius, budget); // never exceed half the shortest side
    var o = cornerParams(radius, smoothing, budget);
    var a = o.a, b = o.b, c = o.c, d = o.d, p = o.p, arc = o.arc, r = o.r;
    var f = function (n) { return (Math.round(n * 1e4) / 1e4); };
    var hStraight = w - 2 * p; // top/bottom straight run
    var vStraight = h - 2 * p; // left/right straight run
    return [
      'M ' + f(w - p) + ' 0',
      'c ' + f(a) + ' 0 ' + f(a + b) + ' 0 ' + f(a + b + c) + ' ' + f(d),
      'a ' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(arc) + ' ' + f(arc),
      'c ' + f(d) + ' ' + f(c) + ' ' + f(d) + ' ' + f(b + c) + ' ' + f(d) + ' ' + f(a + b + c),
      'l 0 ' + f(vStraight),
      'c 0 ' + f(a) + ' 0 ' + f(a + b) + ' ' + f(-d) + ' ' + f(a + b + c),
      'a ' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(-arc) + ' ' + f(arc),
      'c ' + f(-c) + ' ' + f(d) + ' ' + f(-(b + c)) + ' ' + f(d) + ' ' + f(-(a + b + c)) + ' ' + f(d),
      'l ' + f(-hStraight) + ' 0',
      'c ' + f(-a) + ' 0 ' + f(-(a + b)) + ' 0 ' + f(-(a + b + c)) + ' ' + f(-d),
      'a ' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(-arc) + ' ' + f(-arc),
      'c ' + f(-d) + ' ' + f(-c) + ' ' + f(-d) + ' ' + f(-(b + c)) + ' ' + f(-d) + ' ' + f(-(a + b + c)),
      'l 0 ' + f(-vStraight),
      'c 0 ' + f(-a) + ' 0 ' + f(-(a + b)) + ' ' + f(d) + ' ' + f(-(a + b + c)),
      'a ' + f(r) + ' ' + f(r) + ' 0 0 1 ' + f(arc) + ' ' + f(-arc),
      'c ' + f(c) + ' ' + f(-d) + ' ' + f(b + c) + ' ' + f(-d) + ' ' + f(a + b + c) + ' ' + f(-d),
      'z'
    ].join(' ');
  }

  function apply(el) {
    var w = el.offsetWidth;
    var h = el.offsetHeight;
    if (!w || !h) return; // not laid out yet (e.g. hidden) — retry on resize/observe
    var radius = parseFloat(el.getAttribute('data-squircle-radius'));
    if (!(radius > 0)) radius = 20;
    var smoothing = parseFloat(el.getAttribute('data-squircle-smoothing'));
    if (!(smoothing >= 0 && smoothing <= 1)) smoothing = 1; // Figma design uses full (100%) smoothing

    // Skip recompute if size + params are unchanged since last time.
    var sig = w + 'x' + h + ':' + radius + ':' + smoothing;
    if (el._squircleSig === sig) return;
    el._squircleSig = sig;

    var d = squirclePath(w, h, radius, smoothing);
    var val = "path('" + d + "')";
    el.style.webkitClipPath = val;
    el.style.clipPath = val;
  }

  // Only wire clip-path where the browser can honor path() — otherwise the
  // element keeps its border-radius fallback untouched.
  var SUPPORTS = (function () {
    try {
      return (window.CSS && CSS.supports &&
        (CSS.supports('clip-path', "path('M0 0')") ||
         CSS.supports('-webkit-clip-path', "path('M0 0')")));
    } catch (e) { return false; }
  })();

  var ro = (SUPPORTS && 'ResizeObserver' in window)
    ? new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) apply(entries[i].target);
      })
    : null;

  function init(root) {
    if (!SUPPORTS) return;
    root = root || document;
    var els = root.querySelectorAll('[data-squircle]');
    for (var i = 0; i < els.length; i++) {
      apply(els[i]);
      if (ro) ro.observe(els[i]); // observe() is idempotent per element
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Theme editor + async page transitions re-scan.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('page:swapped', function () { init(); });

  // Expose for manual/dynamic use.
  window.PlantaSquircle = { apply: apply, init: init, path: squirclePath };
})();
